/* eslint-disable react-refresh/only-export-components */
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { setAuthToken } from '../services/apiClient'

const AUTH_STORAGE_KEY = 'fts-auth-state'

export interface AuthUser {
  id: string
  name?: string
  email?: string
  avatar?: string
}

interface StoredAuthState {
  user: AuthUser
  token: string
}

interface AuthContextValue {
  user: AuthUser | null
  token: string | null
  isAuthenticated: boolean
  isReady: boolean
  isAuthenticating: boolean
  requiresConfiguration: boolean
  signInWithGoogle: () => void
  signOut: () => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const DEFAULT_SCOPES = 'openid email profile'

async function fetchGoogleProfile(accessToken: string): Promise<AuthUser | null> {
  try {
    const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      return null
    }

    const payload = (await response.json()) as Record<string, unknown>

    const id = typeof payload.sub === 'string' ? payload.sub : undefined
    if (!id) {
      return null
    }

    return {
      id,
      name: typeof payload.name === 'string' ? payload.name : undefined,
      email: typeof payload.email === 'string' ? payload.email : undefined,
      avatar: typeof payload.picture === 'string' ? payload.picture : undefined,
    }
  } catch (error) {
    console.error('Failed to fetch Google profile', error)
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [isAuthenticating, setIsAuthenticating] = useState(false)

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined
  const scopes = (import.meta.env.VITE_GOOGLE_OAUTH_SCOPES as string | undefined) ?? DEFAULT_SCOPES

  const requiresConfiguration = !clientId

  const applyState = useCallback((nextUser: AuthUser | null, nextToken: string | null) => {
    setUser(nextUser)
    setToken(nextToken)

    if (typeof window === 'undefined') {
      return
    }

    if (nextUser && nextToken) {
      const stored: StoredAuthState = { user: nextUser, token: nextToken }
      window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(stored))
    } else {
      window.localStorage.removeItem(AUTH_STORAGE_KEY)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const storedRaw = window.localStorage.getItem(AUTH_STORAGE_KEY)
    if (!storedRaw) {
      return
    }

    try {
      const stored = JSON.parse(storedRaw) as StoredAuthState
      if (stored?.user && stored?.token) {
        applyState(stored.user, stored.token)
      }
    } catch (error) {
      console.warn('Unable to restore authentication state', error)
      window.localStorage.removeItem(AUTH_STORAGE_KEY)
    }
  }, [applyState])

  useEffect(() => {
    setAuthToken(token ?? null)
  }, [token])

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsReady(true)
      return
    }

    if (!clientId) {
      setIsReady(true)
      return
    }

    if (window.google?.accounts?.oauth2) {
      setIsReady(true)
      return
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-google-account-script="true"]')
    if (existingScript && existingScript.dataset.loaded === 'true') {
      setIsReady(true)
      return
    }

    const script = existingScript ?? document.createElement('script')

    const handleReady = () => {
      script.dataset.loaded = 'true'
      setIsReady(true)
    }

    if (!existingScript) {
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.dataset.googleAccountScript = 'true'
      script.addEventListener('load', handleReady, { once: true })
      script.addEventListener(
        'error',
        () => {
          console.error('Google Identity Services failed to load')
          setIsReady(true)
        },
        { once: true },
      )
      document.head.appendChild(script)
    } else {
      existingScript.addEventListener('load', handleReady, { once: true })
    }

    return () => {
      script.removeEventListener('load', handleReady)
    }
  }, [clientId])

  const signInWithGoogle = useCallback(() => {
    if (!clientId) {
      applyState(
        {
          id: 'local-user',
          name: 'Demo User',
          email: 'demo@example.com',
        },
        'mock-access-token',
      )
      return
    }

    const googleOAuth = window.google?.accounts?.oauth2
    if (!googleOAuth) {
      console.error('Google OAuth has not initialised yet')
      return
    }

    setIsAuthenticating(true)

    const client = googleOAuth.initTokenClient({
      client_id: clientId,
      scope: scopes,
      prompt: 'consent',
      callback: async (response) => {
        if ('error' in response) {
          console.error('Google sign-in failed', response)
          setIsAuthenticating(false)
          return
        }

        const accessToken = response.access_token
        const profile = await fetchGoogleProfile(accessToken)

        if (!profile) {
          console.warn('Received token but could not load profile information')
          applyState(null, null)
          setIsAuthenticating(false)
          return
        }

        applyState(profile, accessToken)
        setIsAuthenticating(false)
      },
      error_callback: (error) => {
        console.error('Google sign-in encountered an error', error)
        setIsAuthenticating(false)
      },
    })

    client.requestAccessToken({ prompt: 'consent' })
  }, [applyState, clientId, scopes])

  const signOut = useCallback(() => {
    if (token && window.google?.accounts?.oauth2?.revoke) {
      try {
        window.google.accounts.oauth2.revoke(token, () => undefined)
      } catch (error) {
        console.warn('Failed to revoke Google token', error)
      }
    }

    applyState(null, null)
  }, [applyState, token])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(user && token),
      isReady,
      isAuthenticating,
      requiresConfiguration,
      signInWithGoogle,
      signOut,
    }),
    [isAuthenticating, isReady, requiresConfiguration, signInWithGoogle, signOut, token, user],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuthContext() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}
